/**
 * Вкладка «Уровни» — главная в разделе.
 *
 * Строка = уровень (или вступление): какими шаблонами партнёра снимутся
 * его ролики.
 *
 * ЦЕЛЬ НАСТРОЙКИ — ХАРАКТЕР, а не героиня, и это главное, что здесь надо
 * понимать. Ассистентку игрок создаёт себе сам: выбирает характер, грузит
 * своё фото, даёт имя. Её лицом и снимаются ролики. Студийных героинь
 * в игре нет (убраны 16.09.2026), поэтому вкладками здесь стоят характеры:
 * «Все характеры» плюс три отдельных.
 *
 * Пока вкладками были студийные героини, настройка уходила в никуда:
 * шаблоны назначались им, игра снимала ассистенток игроков по общему
 * набору, тот оставался пустым, и в игре висело вечное «она готовится…».
 *
 * ПЕРВАЯ ЦЕЛЬ — «Все характеры». Назначенное там действует для всех, у кого
 * нет своего; отдельная вкладка характера нужна, только если скромных надо
 * снимать иначе, чем властных. Она же одна правит СОСТАВ: сколько шаблонов
 * выбрано, столько у уровня и роликов (от 1 до `MAX_CLIPS_PER_LEVEL`).
 * У характера свой бывает только шаблон — ролики у всех характеров одни
 * и те же, иначе «клип 2 из 3» означало бы разное у разных игроков.
 *
 * Столбца «Ролики» здесь нет и быть не может: ролик живёт парой
 * (ассистентка, клип), и у одного клипа их столько, сколько игроков по нему
 * снято. Одна строка «готов/нет» на тысячи — неправда ровно в том месте,
 * где решают, тратить кредиты или нет.
 *
 * Переиспользовано, а не написано заново: `DataTable`, бейдж владельца
 * строки — из Economy/Scenes.
 */

import { useEffect, useState } from 'react'
import { MAX_CLIPS_PER_LEVEL } from '@lust/shared'
import { request } from '../../api/client.js'
import {
  PRESET_TARGET_TITLES,
  PRESET_TARGETS,
  type LevelNode,
  type LevelsResponse,
  type PresetTarget,
  type TemplateRow,
} from '../../api/types.js'
import { DataTable, type Column } from '../../components/DataTable.js'
import { Badge, Card, Notice, SourceBadge } from '../../components/ui.js'
import { SetPanel } from './SetPanel.js'

export interface LevelsProps {
  templates: TemplateRow[]
  /** Причина, по которой каталог пуст или неполон — партнёр выключен/недоступен. */
  templatesWarning: string | null
  onChanged: () => void
}

export function Levels({ templates, templatesWarning, onChanged }: LevelsProps): JSX.Element {
  /**
   * Чью настройку смотрим. «Все характеры» стоит по умолчанию: это то, что
   * играется у всех, пока владелец не задал характеру своё.
   */
  const [target, setTarget] = useState<PresetTarget>('all')
  const [data, setData] = useState<LevelsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [version, setVersion] = useState(0)
  /** Сцена, набор которой сейчас правится. null — панель закрыта. */
  const [editing, setEditing] = useState<LevelNode | null>(null)

  // Без флага отмены и без сброса `data` быстрое переключение цели рисовало
  // бы дерево ПРЕЖНЕЙ цели под подсвеченной новой — владелец правил бы
  // не то, что видит.
  useEffect(() => {
    let cancelled = false
    setData(null)
    setError(null)
    setEditing(null)

    request<LevelsResponse>('/admin/generation/levels', { query: { target } })
      .then((result) => {
        if (cancelled) return
        setData(result)
        setError(null)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })

    return () => {
      cancelled = true
    }
  }, [target, version])

  const bump = (): void => {
    setVersion((v) => v + 1)
    onChanged()
  }

  const nameOf = new Map(templates.map((t) => [t.id, t.name]))

  const columns: Column<LevelNode>[] = [
    {
      key: 'level',
      title: 'Уровень',
      render: (row) => `Уровень ${String(row.levelOrd)} · ${row.levelTitle}`,
    },
    {
      key: 'set',
      title: 'Чем снимать',
      render: (row) =>
        row.sceneId === null ? (
          <span className="muted">—</span>
        ) : (
          <SetCell row={row} nameOf={nameOf} target={target} onEdit={() => setEditing(row)} />
        ),
    },
  ]

  return (
    <div className="stack">
      {templatesWarning !== null && <Notice tone="warn">{templatesWarning}</Notice>}

      <div className="btn-group">
        {PRESET_TARGETS.map((option) => (
          <button
            key={option}
            type="button"
            className={option === target ? 'is-active' : undefined}
            onClick={() => setTarget(option)}
          >
            {PRESET_TARGET_TITLES[option]}
          </button>
        ))}
      </div>

      {/*
        Подпись обязательна: без неё «Все характеры» читается как сводка
        по всем вкладкам, а это конкретная настройка — та, что достаётся
        каждой ассистентке, характеру которой не назначено своего.
      */}
      <p className="muted" style={{ margin: 0, fontSize: 12 }}>
        {target === 'all'
          ? `Шаблон по умолчанию: им снимаются ассистентки всех характеров, пока характеру не назначено своё. Ассистентку игрок создаёт сам — характер, своё фото, имя, — и ролики снимаются её лицом. Сколько роликов у уровня — от 1 до ${String(MAX_CLIPS_PER_LEVEL)} — задаётся здесь же: сколько шаблонов выбрано, столько роликов уровень и показывает.`
          : `Только характер «${PRESET_TARGET_TITLES[target]}». Где ничего не назначено, действует «Все характеры» — такие ролики помечены «как у всех».`}
      </p>

      {error !== null && <Notice tone="error">{error}</Notice>}

      {editing !== null && editing.sceneId !== null && (
        <SetPanel
          sceneId={editing.sceneId}
          // В заголовке панели — УРОВЕНЬ, а не сцена: владелец пришёл сюда
          // из строки уровня, и увидеть в шапке другое название значило бы
          // гадать, туда ли он попал.
          sceneTitle={`Уровень ${String(editing.levelOrd)} · ${editing.levelTitle}`}
          target={target}
          clips={editing.clips}
          onClose={() => setEditing(null)}
          onSaved={bump}
        />
      )}

      {data === null ? (
        <Card>
          <p className="muted">Загрузка уровней…</p>
        </Card>
      ) : (
        data.chapters.map((chapter) => (
          <Card key={chapter.id} title={`${String(chapter.ord)}. ${chapter.title}`} padded={false}>
            <DataTable columns={columns} rows={chapter.levels} rowKey={rowKey} empty="В этой главе нет уровней." />
          </Card>
        ))
      )}
    </div>
  )
}

function rowKey(row: LevelNode): string {
  return row.sceneId ?? `no-scene-${row.levelId ?? 'x'}`
}

/**
 * Ячейка настройки: чипы шаблонов в порядке показа и кнопка правки.
 *
 * Бейдж «как у всех» — не украшение. Пока характеру не назначено своё, он
 * показывает ровно то же, что и «Все характеры», и без пометки владелец
 * решил бы, что уже настроил его персонально.
 */
function SetCell({
  row,
  nameOf,
  target,
  onEdit,
}: {
  row: LevelNode
  nameOf: Map<string, string>
  target: PresetTarget
  onEdit: () => void
}): JSX.Element {
  const inherited = row.clips.length > 0 && row.clips.every((clip) => clip.inherited)

  return (
    <div className="stack stack--sm">
      <div className="row" style={{ flexWrap: 'wrap' }}>
        {row.clips.length === 0 && <span className="muted">у уровня нет роликов</span>}
        {row.clips.map((clip, index) => (
          <Badge key={clip.clipId} tone={clip.promptPresetId === null ? 'warn' : 'muted'}>
            {index + 1} ·{' '}
            {clip.promptPresetId === null
              ? 'шаблон не выбран'
              : nameOf.get(clip.promptPresetId) ?? clip.promptPresetId}
          </Badge>
        ))}
      </div>

      <div className="row">
        {/*
          На пустом уровне кнопка живая только у цели «Все характеры»:
          состав роликов правит она одна, и это единственное место, откуда
          у уровня вообще могут появиться ролики. Характеру назначать
          нечего, пока их нет.
        */}
        <button
          className="btn btn--sm"
          type="button"
          disabled={row.clips.length === 0 && target !== 'all'}
          onClick={onEdit}
        >
          {row.clips.length === 0 ? 'завести ролики' : 'выбрать шаблоны'}
        </button>
        {/* На пустом уровне бейджи молчат: показывать нечего ни этому
            характеру, ни остальным. */}
        {row.clips.length === 0 ? null : target === 'all' ? (
          /* Владелец строки: файл сценария или человек. Тот же бейдж,
             что в Economy у строк game_configs. */
          <SourceBadge source={row.clips.some((clip) => clip.source === 'admin') ? 'admin' : 'seed'} />
        ) : inherited ? (
          <Badge tone="muted">как у всех</Badge>
        ) : (
          <SourceBadge source="admin" />
        )}
      </div>
    </div>
  )
}
